"use client";

import { useMemo } from "react";
import { useReadContracts } from "wagmi";
import { erc20Abi } from "@/lib/onchain/generated";

export function useTokenBalances(params: {
  chainId: number;
  tokens: `0x${string}`[];
  address?: `0x${string}`;
}) {
  const address = params.address;
  const contracts = useMemo(() => {
    if (!address) return [] as any[];
    return params.tokens.map((t) => ({
      address: t,
      abi: erc20Abi,
      functionName: "balanceOf" as const,
      args: [address as `0x${string}`] as const,
      chainId: params.chainId,
    }));
  }, [address, params.chainId, params.tokens]);

  const { data, isLoading } = useReadContracts({
    contracts,
    query: { enabled: !!address && params.tokens.length > 0 },
  });

  const balances = useMemo(() => {
    const map = new Map<`0x${string}`, bigint>();
    if (!data) return map;
    params.tokens.forEach((t, i) => {
      const r = data[i];
      const val = r && r.status === "success" ? (r.result as bigint) : 0n;
      map.set(t, val);
    });
    return map;
  }, [data, params.tokens]);

  return { balances, isLoading } as const;
}

export function useErc20Balance(params: { chainId: number; token: `0x${string}` }) {
  const { balances, isLoading } = useTokenBalances({
    chainId: params.chainId,
    tokens: [params.token],
  });
  return { balance: balances.get(params.token) || 0n, isLoading } as const;
}
